import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTemplateOrCalculation } from '../../../hooks/useTemplateOrCalculation';
import { setSubpage } from '../../../routes/routerSlice';
import { Helmet } from 'react-helmet-async';
import { Label, MainContent, MainContentWrapper, RowWrap, Section } from '@ydistri/ds';
import SectionOverallStatistics from './SectionOverallStatistics';
import SectionRoutes from './SectionRoutes';
import SectionStores from './SectionStores';
import SectionRegions from './SectionRegions';
import SectionSkuClasses from './SectionSkuClasses';
import SectionStoreTypes from './SectionStoreTypes';
import StatisticsModal from './modal/StatisticsModal';
import { ReduxState, useAppDispatch } from '../../../store';
import { CalculationDetailSubpages } from '../../Calculations/calculationsLib';
import { Space } from 'antd';
import { useGetDepartmentsQuery } from '../../../apis/apiLists';
import DepartmentSelector from '../../Detail/sections/DetailContentHeaderSection/components/DepartmentSelector/DepartmentSelector';
import { setClusterType, setDepartmentId } from './statisticsSlice';
import { CalculationStatisticClusterType } from '@ydistri/api-sdk';
import { SegmentedValue } from 'antd/es/segmented';
import { useApplicationData } from '../../../hooks/useApplicationData';
import { useCurrency } from '../../../hooks/useCurrency';
import { statisticsDepartmentOptions } from './lib/calculationStatsLib';
import ButtonOptions from '../../../components/global/ButtonOptions/ButtonOptions';

export const clusterTypeOptions = [
  {
    label: 'Stores',
    value: CalculationStatisticClusterType.Store,
  },
  {
    label: 'Store-departments',
    value: CalculationStatisticClusterType.StoreDepartment,
  },
];

const CalculationStatistics: React.FC = () => {
  const dispatch = useAppDispatch();
  const { departments: hasDepartments } = useApplicationData();
  const templateOrCalculation = useTemplateOrCalculation();
  const { data: departments } = useGetDepartmentsQuery(templateOrCalculation);
  const modalType = useSelector((state: ReduxState) => state.statistics.modalOpened);
  const departmentId = useSelector((state: ReduxState) => state.statistics.departmentId);
  const clusterType = useSelector((state: ReduxState) => state.statistics.clusterType);
  const currencyId = useCurrency();

  useEffect(() => {
    dispatch(setSubpage(CalculationDetailSubpages.STATISTICS));
  }, [dispatch]);

  const onDepartmentChange = useCallback(
    (did: number | undefined) => {
      dispatch(setDepartmentId(did));
    },
    [dispatch],
  );

  const onClusterTypeChange = useCallback(
    (ct: SegmentedValue) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we need to retype the value
      dispatch(setClusterType(ct as CalculationStatisticClusterType));
    },
    [dispatch],
  );

  const departmentSelectHeaderActions = useMemo(() => {
    const options = departments ? statisticsDepartmentOptions(departments.array, currencyId) : [];

    return (
      <Space>
        <Label>Group by: </Label>
        <ButtonOptions
          options={clusterTypeOptions}
          value={clusterType}
          setValue={onClusterTypeChange}
        />
        <DepartmentSelector options={options} value={departmentId} onChange={onDepartmentChange} />
      </Space>
    );
  }, [clusterType, currencyId, departmentId, departments, onClusterTypeChange, onDepartmentChange]);

  return (
    <>
      <Helmet title={`Calculation ${templateOrCalculation.id} - Statistics`} />
      <MainContentWrapper $flexDirection="column">
        {hasDepartments && (
          <Section
            $ratio={0}
            data-id="StatisticsDepartmentSelector"
            header={`Statistics for ${
              departmentId
                ? 'department "' + (departments?.object[departmentId]?.name ?? '') + '"'
                : 'all departments'
            } grouped by ${
              clusterType === CalculationStatisticClusterType.Store ? 'stores' : 'store-departments'
            }`}
            headerActions={departmentSelectHeaderActions}
          />
        )}
        <MainContent>
          <Section data-id="StatisticsOverall">
            <RowWrap>
              <SectionOverallStatistics />
              <SectionRoutes />
            </RowWrap>
          </Section>
          <Section data-id="StatisticsStores">
            <SectionStores />
          </Section>
          <SectionSkuClasses />
          <SectionRegions />
          <SectionStoreTypes />
          {modalType && <StatisticsModal statisticsModalType={modalType} />}
        </MainContent>
      </MainContentWrapper>
    </>
  );
};

export default CalculationStatistics;
